import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import styled from "styled-components";
import ClearAll from "./ClearAll";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import { TOOL_PANELS } from "../../components/EquipmentList/Constants/constants";
import {
  OwcIconButton,
  OwcTooltip,
  OwcTypography,
  OwcIcon,
  OwcSelectDropdown,
  OwcListItem,
  OwcEditableDropdown
} from "@one/react";
import {
  LIST_GROUP_BY_BOOKING,
  LIST_GROUP_BY_EQUIPMENT,
  LIST_GROUP_BY_NONE,
  LIST_VIEW,
  SHOW_ADVANCE_FILTER_TOOLTIP,
  MY_LAB,
  FORCE_TIMELINE_RANGE_KEY,
} from "../../constants";
import Search from "./Search";
import moment from "moment-timezone";
import BookingViewChanger from "../booking-calendars/booking-view-changer/BookingViewChanger";
import { withApollo } from "react-apollo";
import { LIST_DIGITAL_LAB_BOOKING_USERS } from "../../gql/bookingapi";
import { getAllData } from "../../utils/helpers/fetching";
import { get4WeekStartAndEndInISOString } from "../../utils/helpers/text";
import { IconButton } from "@mui/material";
import ColumnsOutlinedGrey from "../../components/EquipmentList/icons/ColumnsOutlinedGrey";
import ColumnsOutlinedActive from "../../components/EquipmentList/icons/ColumnsOutlinedActive";
import FilterOutlinedGrey from "../../components/EquipmentList/icons/FilterOutlinedGrey";
import FilterOutlinedActive from "../../components/EquipmentList/icons/FilterOutlinedActive";
import GoogleCalendarSync from "../../components/GoogleCalendarSync";
import GoogleCalendarErrorBoundary from "../../components/GoogleCalendarErrorBoundary";
import SigneeFilter from "./SigneeFilter";
import { handleOnCellClick } from "../booking-calendars/helpers";
import styles from "./BookingFilter.module.scss";
import { useSelector } from "react-redux";
import {BOOKING_STORAGE_KEYS, getPersistedFilter} from "../../utils/store/bookingFilterPersistence";
import TvModeToggle from "./TvModeToggle";
import {readForcedTimelineRangeFromStorage} from "./ViewSwitchingUtils";
import {
  buildSavedFilterOptions,
  computeIsResetDisabled,
  getInitialSavedFilterSelection,
  getResetSavedFilterValue,
  getSavedFilterChangeResult,
  getSigneeFilterChangeResult,
  getTvDateLabel,
} from "./BookingFilter.utils";

const BookingFilterStyled = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  padding-left: 10px;
  flex-wrap: wrap;
  gap: 10px
`;

const AdvanceFilterButton = ({ setShowAdvanceFilter }) => (
  <>
    <OwcTooltip anchor={"advance-filter"} placement="top">
      {SHOW_ADVANCE_FILTER_TOOLTIP}
    </OwcTooltip>
    <OwcIconButton
      id="advance-filter"
      data-testid="advance-filter-button"
      style={{ marginRight: 8 }}
      icon=" parameter_set"
      type="filled"
      onClick={() => {
        setShowAdvanceFilter((prev) => !prev);
      }}
      flat
    />
  </>
);

const getPageTitle = ({ isGroupView, savedFilter }) => {
  if (isGroupView) return `Group views - ${savedFilter}`;
  return "All bookings";
};

const PageHeader = ({ isTvMode, tvDateLabel, isGroupView, savedFilter, isTimelineView }) => (
  <div className={`${styles.pageTitle} ${isTvMode ? styles.pageTitleTv : ''}`}>
    {isTvMode ? (
      <OwcTypography
        style={{
          fontFamily: 'Roche Sans',
          fontWeight: 500,
          fontStyle: 'normal',
          fontSize: 16,
          color: '#0B41CD'
        }}
      >
        {tvDateLabel}
      </OwcTypography>
    ) : (
      <OwcTypography variant="button">
        {getPageTitle({ isGroupView, savedFilter })}
      </OwcTypography>
    )}

    <TvModeToggle visible={isTimelineView} />
  </div>
);

const CompactTimelineControls = ({ model }) => {
  const {
    isListGroupedByBookingView,
    savedFilterOptions,
    isGroupView,
    handleSavedFilterChange,
    signeeFilter,
    allUsers,
    handleSigneeFilterChange,
    handleReset,
    isResetDisabled,
    showAdvanceFilter,
    setShowAdvanceFilter,
    showGoogleCalendarSync,
    isEquipmentView,
    view,
    setView,
    setTempRowData,
    setIsEquipmentView,
    listGroupByView,
    setListGroupByView,
    isTimelineView,
    savedFilter,
  } = model;

  return (
    <div style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px"
  }}>
    <BookingFilterStyled>
      <AdvanceFilterButton setShowAdvanceFilter={setShowAdvanceFilter} />

      {showAdvanceFilter && (
        <>
          {!isListGroupedByBookingView && (<div>
            <OwcSelectDropdown
              style={{ width: 200 }}
              label="Select predefined filter"
              dataTestid="equipment-filter-dropdown"
              name="savedFilter"
              disabled={isGroupView}
              onSelectChange={(ev) => {
                const selectedOption = savedFilterOptions[ev.detail[0]];
                if (selectedOption) {
                  handleSavedFilterChange(selectedOption.name);
                }
              }}
            >
              {savedFilterOptions.map((option) => (
                <OwcListItem
                  key={option.name}
                  value={option.name}
                  selected={savedFilter === option.name}
                  data-testid={"equipment-filter-menu-item-" + (option.name || "all")}
                >
                  {option.label}
                </OwcListItem>
              ))}
            </OwcSelectDropdown>

          </div>)}

          <SigneeFilter
            signeeFilter={signeeFilter}
            allUsers={allUsers}
            onSigneeFilterChange={handleSigneeFilterChange}
            width={200}
          />
          <div style={{ margin: 'auto 0', marginLeft: 8 }}>
            <ClearAll
              onClickCallback={handleReset}
              disabled={isResetDisabled} />
          </div>
        </>
      )}
    </BookingFilterStyled>

    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {showGoogleCalendarSync && isEquipmentView && (
        <GoogleCalendarErrorBoundary key={`calendar-error-boundary-${isEquipmentView}`}>
          <GoogleCalendarSync key={`calendar-sync-${isEquipmentView}-${Date.now()}`} />
        </GoogleCalendarErrorBoundary>
      )}

      <BookingViewChanger
        view={view}
        setView={setView}
        setTempRowData={setTempRowData}
        isEquipmentView={isEquipmentView}
        setIsEquipmentView={setIsEquipmentView}
        listGroupByView={listGroupByView}
        setListGroupByView={setListGroupByView}
        isToggle={true} />

      <div className={styles.lowResTvModeToggle}>
        <TvModeToggle visible={isTimelineView} />
      </div>

    </div>
  </div>
  );
};

const toggleSidebarPanel = ({
  panel,
  isTargetActive,
  setTargetActive,
  setOtherActive,
  showSideBar,
  setShowSideBar,
  setCurrentSideBarPanel,
}) => {
  setOtherActive(() => false);
  if (showSideBar) {
    setShowSideBar(false);
    setCurrentSideBarPanel(panel);
    if (!isTargetActive) {
      setTimeout(() => {
        setShowSideBar(true);
      });
    }
    setTargetActive((prevState) => !prevState);
    return;
  }

  setCurrentSideBarPanel(panel);
  setTargetActive(() => true);
  setShowSideBar(() => true);
};

const ListFilterDropdownSection = ({ model }) => {
  const {
    isListGroupedByBookingView,
    isGroupView,
    savedFilterOptions,
    handleSavedFilterChange,
    savedFilter,
    isListGroupedByNoneView,
    setIsColumnActive,
    showSideBar,
    setShowSideBar,
    setCurrentSideBarPanel,
    isFilterActive,
    setIsFilterActive,
  } = model;

  return (
    <div style={{ display: 'flex' }}>
    {!isListGroupedByBookingView && (<OwcSelectDropdown
      style={{ width: 200 }}
      label="Select predefined filter"
      dataTestid="equipment-filter-dropdown"
      name="savedFilter"
      disabled={isListGroupedByBookingView || isGroupView}
      onSelectChange={(ev) => {
        if (isGroupView) return;
        const selectedOption = savedFilterOptions[ev.detail[0]];
        if (selectedOption) {
          handleSavedFilterChange(selectedOption.name);
        }
      }}
    >
      {savedFilterOptions.map((option) => (
        <OwcListItem
          key={option.name}
          value={option.name}
          selected={savedFilter === option.name}
          data-testid={"equipment-filter-menu-item-" + (option.name || "all")}
        >
          {option.label}
        </OwcListItem>
      ))}
    </OwcSelectDropdown>)}

    {isListGroupedByNoneView && (<IconButton
      id="filter-list"
      onClick={() => {
        toggleSidebarPanel({
          panel: TOOL_PANELS.filters,
          isTargetActive: isFilterActive,
          setTargetActive: setIsFilterActive,
          setOtherActive: setIsColumnActive,
          showSideBar,
          setShowSideBar,
          setCurrentSideBarPanel,
        });
      }}
    >
      {!isFilterActive && (<><FilterOutlinedGrey /></>)}
      {isFilterActive && (<><FilterOutlinedActive /></>)}
    </IconButton>)}
    </div>
  );
};

const ListColumnDropdownSection = ({ model }) => {
  const {
    isListGroupedByBookingView,
    displayPreset,
    displayPresetOptions,
    handleDisplayPresetChange,
    isListGroupedByNoneView,
    isColumnActive,
    setIsColumnActive,
    showSideBar,
    setShowSideBar,
    setCurrentSideBarPanel,
    setIsFilterActive,
  } = model;

  if (isListGroupedByBookingView) return null;

  return (
    <div style={{ display: 'flex' }}>
      <OwcEditableDropdown
        key={displayPreset?.name}
        style={{ width: 200 }}
        label="Predefined columns"
        disabled={isListGroupedByBookingView}
        dataTestid="Predefined columns"
        name="Predefined columns"
        onSelectChange={(ev) => {
          const selected = displayPresetOptions[ev.detail];
          if (selected) {
            handleDisplayPresetChange(selected);
          } else handleDisplayPresetChange(null);
        }}>
        {displayPresetOptions.map((option) => (
          <OwcListItem key={option.name} value={option.name} selected={displayPreset?.name === option.name}>
            {option.label}
          </OwcListItem>
        ))}
      </OwcEditableDropdown>
      {isListGroupedByNoneView && (<IconButton
        id="column-filter"
        onClick={() => {
          toggleSidebarPanel({
            panel: TOOL_PANELS.columns,
            isTargetActive: isColumnActive,
            setTargetActive: setIsColumnActive,
            setOtherActive: setIsFilterActive,
            showSideBar,
            setShowSideBar,
            setCurrentSideBarPanel,
          });
        }}
      >
        {!isColumnActive && (<><ColumnsOutlinedGrey /></>)}
        {isColumnActive && (<><ColumnsOutlinedActive /></>)}
      </IconButton>)}
    </div>
  );
};

const ListSigneeAndClearSection = ({ model }) => {
  const {
    signeeFilter,
    allUsers,
    handleSigneeFilterChange,
    handleReset,
    isResetDisabled,
  } = model;

  return (
    <>
    <SigneeFilter
      signeeFilter={signeeFilter}
      allUsers={allUsers}
      onSigneeFilterChange={handleSigneeFilterChange}
      width={200}
    />
    <div style={{ margin: 'auto 0', marginLeft: 16 }}>
      <ClearAll
        onClickCallback={handleReset}
        disabled={isResetDisabled}
      />
    </div>
  </>
  );
};

const ListAdvancedSection = ({ model }) => (
  <>
    <ListFilterDropdownSection model={model} />
    <ListColumnDropdownSection model={model} />
    <ListSigneeAndClearSection model={model} />
  </>
);

const ListViewControls = ({ model }) => {
  const {
    isAnyListView,
    setSearchQuery,
    searchQuery,
    fetching,
    selectedDateRange,
    dispatchAction,
    setTempRowData,
    setDisableResetAll,
    setSelectedDateRange,
    dataTimeZone,
    displayTimeZone,
    showAdvanceFilter,
    setShowAdvanceFilter,
    setListGroupByView,
    listGroupByView,
    view,
    setView,
    isEquipmentView,
    setIsEquipmentView,
  } = model;

  if (!isAnyListView) return null;

  return (
    <>
      <BookingFilterStyled data-testid="booking-filter-admin" style={{paddingLeft:'16px'}}>
        <Search
          onChange={(e) => setSearchQuery(e.detail)}
          onClear={() => setSearchQuery("")}
          label="Search all columns"
          placeholder="Search all columns"
          testid={"common-search-drop-booking"}
          fetching={fetching}
          compact={false}
          searchQuery={searchQuery}
        ></Search>

        <div style={{ padding: "0 5px 0 5px" }} className="date-picker-my-booking">
          <Datepicker
            id="datepicker-admin-panel"
            controls={["calendar"]}
            value={selectedDateRange}
            inputStyle="box"
            style={{ height: "5px" }}
            select="range"
            display="anchored"
            inRangeInvalid={true}
            rangeEndInvalid={true}
            startIcon={<OwcIcon name="date" type="outlined" />}
            onTempChange={handleOnCellClick}
            headerText="Experiment date(range)"
            label={"Date range"}
            touchUi={"auto"}
            dateFormat="D-MMM-YYYY"
            onClose={(param) => {
              if (param?.value) {
                dispatchAction({ type: "clearAll" });
                setTempRowData([]);
                setDisableResetAll(false);
                setSelectedDateRange(param?.value);
              }
            }}
            timezonePlugin={momentTimezone}
            dataTimezone={dataTimeZone}
            displayTimezone={displayTimeZone}
            buttons={["cancel", "set"]}
            compact
            exclusiveEndDates={false}
          />
        </div>

        <AdvanceFilterButton setShowAdvanceFilter={setShowAdvanceFilter} />

        {showAdvanceFilter && (
          <ListAdvancedSection model={model} />
        )}
      </BookingFilterStyled>

      <BookingViewChanger setTempRowData={setTempRowData} view={view} setView={setView} setListGroupByView={setListGroupByView} listGroupByView={listGroupByView} isEquipmentView={isEquipmentView} setIsEquipmentView={setIsEquipmentView} />
    </>
  );
};

const BookingFilterView = ({ model }) => {
  const {
    isTvMode,
    tvDateLabel,
    isGroupView,
    savedFilter,
    isTimelineView,
    isAnyListView,
    showInvalidFilter,
  } = model;

  if (showInvalidFilter) return <></>;

  return (
    <>
      <PageHeader
        isTvMode={isTvMode}
        tvDateLabel={tvDateLabel}
        isGroupView={isGroupView}
        savedFilter={savedFilter}
        isTimelineView={isTimelineView}
      />

      {(!isAnyListView && !isTvMode) && (
        <CompactTimelineControls model={model} />
      )}

      {!isTvMode && (<div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: "10px"
        }}
      >
        <ListViewControls model={model} />
      </div>)}
    </>
  );
};

const BookingFilter = ({
 client,
 setResetAll,
 showAdvanceFilter,
 setShowAdvanceFilter,
 selectedDateRange,
 setSelectedDateRange,
 isTimelineView,
 setDisableResetAll,
 setEnableResetAll,
 enableResetAll,
 searchQuery,
 setSearchQuery,
 setTempRowData,
 view,
 setView,
 isEquipmentView,
 setIsEquipmentView,
 listGroupByView,
 setListGroupByView,
 savedFilter,
 setSavedFilter,
 signeeFilter,
 setSigneeFilter,
 displayPreset,
 setDisplayPreset,
 storageKeys = BOOKING_STORAGE_KEYS,
 storageOptions = {},
 groupName,
 isGroupView = false,
 showInvalidFilter = false,
 userFilters,
 userDisplayColumnMaster,
 myLabFilter, setCurrentSideBarPanel, setShowSideBar, showSideBar,
 isFilterActive, setIsFilterActive, isColumnActive, setIsColumnActive,
 showGoogleCalendarSync = true,  // New prop to control Google Calendar sync visibility
}) => {
  momentTimezone.moment = moment;
  const user = useSelector((state) => state.user);
  const isTvMode = useSelector((state) => state?.bookingInstruments?.isTvMode);

  const { fetching, dispatchAction } = useContext(PaginationContext);
  const dataTimeZone = "utc";
  const displayTimeZone = user.timezone;
  const [allUsers, setAllUsers] = useState([]);
  const isListGroupedByBookingView = view === LIST_VIEW && listGroupByView === LIST_GROUP_BY_BOOKING;
  const isListGroupedByNoneView = view === LIST_VIEW && (listGroupByView === LIST_GROUP_BY_NONE || listGroupByView === LIST_GROUP_BY_EQUIPMENT);
  const isAnyListView = isListGroupedByBookingView || isListGroupedByNoneView;

  const displayPresetOptions = useMemo(() => {
    return (userDisplayColumnMaster || [])
      .map((p) => ({ name: p.displayName + p.userID, label: p.displayName, definition: p.displayDefinition }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [userDisplayColumnMaster]);

  const getDefaultTimeRange = useCallback(() => {
    return [
      get4WeekStartAndEndInISOString("start", user.timezone),
      get4WeekStartAndEndInISOString("end", user.timezone),
    ];
  }, [user.timezone]);

  const setDefaultTimeRange = useCallback(() => {
    setSelectedDateRange(getDefaultTimeRange());
  }, [getDefaultTimeRange, setSelectedDateRange]);

  const handleDisplayPresetChange = useCallback((selectedSavedColumn) => {
    setDisplayPreset(selectedSavedColumn); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savedFilterOptions = useMemo(
    () => buildSavedFilterOptions({ myLabFilter, userFilters, signeeFilter }),
    [signeeFilter, myLabFilter, userFilters],
  );
  const savedFilterStorageKey = storageKeys.SAVED_FILTER;

  useEffect(() => {
    const nextSavedFilter = getInitialSavedFilterSelection({
      savedFilter,
      savedFilterOptions,
      isGroupView,
      savedFilterStorageKey,
      storageOptions,
      getPersistedFilter,
    });
    if (nextSavedFilter !== null) setSavedFilter(nextSavedFilter);
  }, [savedFilterOptions, savedFilter, setSavedFilter, isGroupView, savedFilterStorageKey, storageOptions]);

  useEffect(() => {
    if (isTvMode) return;
    try {
      const parsed = readForcedTimelineRangeFromStorage();
      if (!parsed) return;
      const { dateFrom, dateTo } = parsed;
      const tz = user?.timezone || "UTC";
      const fromStr = moment(dateFrom).tz(tz).format();
      const toStr = moment(dateTo).tz(tz).format();
      if (!moment(fromStr).isValid() || !moment(toStr).isValid()) return;
      dispatchAction({ type: "clearAll" });
      setTempRowData([]);
      setDisableResetAll(false);
      setSelectedDateRange([fromStr, toStr]);
    } catch (e) {
    } finally {
      sessionStorage.removeItem(FORCE_TIMELINE_RANGE_KEY);
    }// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isTvMode, user?.timezone]);


  const loadUsers = useCallback(async () => {
    try {
      const { items } = await getAllData({
        client,
        query: LIST_DIGITAL_LAB_BOOKING_USERS,
        dataPath: ["data", "listDigitalLabBookingUsers"],
        variables: { limit: 1000 },
        cache: true,
      });

      setAllUsers(items || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, [client]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!signeeFilter && allUsers.length > 0) {
      // Default to "All users" (empty string)
      setSigneeFilter("");
    }

    if (!savedFilter && savedFilterOptions.length > 0) {
      const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
      if (myLabOption && !isTvMode) {
        setSavedFilter(MY_LAB);
      } else {
        setSavedFilter(savedFilterOptions[0].name);
      }
    }
  }, [myLabFilter, userFilters, allUsers, signeeFilter, savedFilter, savedFilterOptions, setSavedFilter, setSigneeFilter, isTvMode]);

  const handleSavedFilterChange = useCallback((selectedFilterName) => {
    const { shouldIgnoreSelection, shouldUpdateSavedFilter, nextSigneeFilter } =
      getSavedFilterChangeResult({
        selectedFilterName,
        signeeFilter,
        isGroupView,
        allUsers,
        userId: user.user,
      });

    if (shouldIgnoreSelection) {
      return;
    }
    if (shouldUpdateSavedFilter) setSavedFilter(selectedFilterName);
    if (nextSigneeFilter) setSigneeFilter(nextSigneeFilter);

    dispatchAction({ type: "clearAll" });
    setTempRowData([]);
    setDisableResetAll(false);
  }, [signeeFilter, user.user, allUsers, setSavedFilter, setSigneeFilter, dispatchAction, setTempRowData, setDisableResetAll, isGroupView]);

  const handleSigneeFilterChange = useCallback((selectedSignee) => {
    const { nextSigneeFilter, shouldSwitchToMyLab } = getSigneeFilterChangeResult({
      selectedSignee,
      allUsers,
      savedFilter,
    });

    setSigneeFilter(nextSigneeFilter);
    if (shouldSwitchToMyLab) setSavedFilter(MY_LAB);

    dispatchAction({ type: "clearAll" });
    setTempRowData([]);
    setDisableResetAll(false);
  }, [allUsers, savedFilter, setSigneeFilter, setSavedFilter, dispatchAction, setTempRowData, setDisableResetAll]);

  const handleReset = useCallback(() => {
    const defaultSavedFilter = getResetSavedFilterValue({
      isGroupView,
      savedFilterOptions,
    });
    setSavedFilter(defaultSavedFilter);

    // Reset to "All users" (empty string)
    setSigneeFilter("");

    setDefaultTimeRange();
    setSearchQuery("");
    setResetAll(prev => !prev);
    setEnableResetAll(false)
    setTempRowData([]);
    setDisplayPreset(null)
    setDisableResetAll(true);

    dispatchAction({ type: "clearAll" }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterOptions, isGroupView, setSavedFilter, setSigneeFilter, setDefaultTimeRange, setSearchQuery, setResetAll, setTempRowData, setDisableResetAll, setDisplayPreset, dispatchAction, setEnableResetAll]);

  // Add effect to handle view switching
  useEffect(() => {
    // Clear temporary data when switching views to prevent stale data

    setTempRowData([]);
    dispatchAction({ type: "clearAll" });
  }, [view, isEquipmentView, setTempRowData, dispatchAction]);

   const tvDateLabel = useMemo(() => getTvDateLabel(user?.timezone), [user?.timezone]);


  // Add effect to handle groupName changes in group view
  useEffect(() => {
    if (isGroupView && groupName && groupName !== savedFilter && savedFilterOptions.find((option) => option.name === groupName)) {
      setSavedFilter(groupName);
      // Clear temporary data when group changes
      setTempRowData([]);
      dispatchAction({ type: "clearAll" });
      setDisableResetAll(false);
    }
  }, [groupName, isGroupView, savedFilter, setSavedFilter, setTempRowData, dispatchAction, setDisableResetAll, savedFilterOptions]);

  const isResetDisabled = useMemo(() => {
    return computeIsResetDisabled({
      savedFilterOptions,
      enableResetAll,
      signeeFilter,
      savedFilter,
      searchQuery,
      displayPreset,
      isGroupView,
      selectedDateRange,
      getDefaultDateRange: getDefaultTimeRange,
    });
  }, [savedFilterOptions, enableResetAll, signeeFilter, savedFilter, searchQuery, displayPreset, isGroupView, selectedDateRange, getDefaultTimeRange]);

  const filterViewModel = {
    isTvMode,
    tvDateLabel,
    isGroupView,
    savedFilter,
    isTimelineView,
    isAnyListView,
    showAdvanceFilter,
    isListGroupedByBookingView,
    savedFilterOptions,
    handleSavedFilterChange,
    signeeFilter,
    allUsers,
    handleSigneeFilterChange,
    handleReset,
    isResetDisabled,
    showGoogleCalendarSync,
    isEquipmentView,
    view,
    setView,
    setTempRowData,
    setIsEquipmentView,
    listGroupByView,
    setListGroupByView,
    searchQuery,
    setSearchQuery,
    fetching,
    selectedDateRange,
    dispatchAction,
    setDisableResetAll,
    setSelectedDateRange,
    dataTimeZone,
    displayTimeZone,
    isListGroupedByNoneView,
    setIsColumnActive,
    showSideBar,
    setShowSideBar,
    setCurrentSideBarPanel,
    isFilterActive,
    setIsFilterActive,
    displayPreset,
    displayPresetOptions,
    handleDisplayPresetChange,
    isColumnActive,
    showInvalidFilter,
    setShowAdvanceFilter,
  };

  return (
    <BookingFilterView model={filterViewModel} />
  );
};

export default withApollo(BookingFilter);
